import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { downloadAuthenticated } from "../lib/download";
import PageHeader from "../components/PageHeader";
import CompanyPecBanner from "../components/CompanyPecBanner";
import EmptyState from "../components/EmptyState";
import LoadingState from "../components/LoadingState";
import { toast } from "sonner";
import { Download, LayoutGrid, List, Filter } from "lucide-react";

const STATUSES = [
    "Nuova",
    "In revisione",
    "In attesa di informazioni",
    "Da verificare con il fornitore",
    "Da verificare con il legale",
    "Escalata",
    "Falso positivo",
    "Chiusa",
];
const PRIORITIES = ["Critica", "Alta", "Media", "Bassa"];

function priorityBadge(p) {
    const map = {
        Critica: "bg-[#DC2626] text-white",
        Alta: "bg-[#F59E0B] text-white",
        Media: "bg-[#2563EB] text-white",
        Bassa: "bg-[#64748B] text-white",
    };
    return <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${map[p] || "bg-slate-200"}`}>{p}</span>;
}

function statusBadge(s) {
    const map = {
        "Nuova": "bg-blue-100 text-blue-800",
        "In revisione": "bg-amber-100 text-amber-800",
        "Escalata": "bg-red-100 text-red-800",
        "Falso positivo": "bg-slate-100 text-slate-800",
        "Chiusa": "bg-green-100 text-green-800",
    };
    return <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${map[s] || "bg-slate-100 text-slate-700"}`}>{s}</span>;
}

function ReviewsTable({ items, loading, onOpen }) {
    if (loading) return <LoadingState />;
    if (!items.length) return <EmptyState title="Nessuna revisione" description="Le revisioni verranno create automaticamente per PEC ad alto rischio operativo." />;
    return (
        <div className="bg-white border border-[#DCE3EE] rounded-xl overflow-hidden">
            <table className="w-full text-sm" data-testid="reviews-table">
                <thead className="bg-[#F5F7FB] text-[11px] uppercase text-[#64748B]">
                    <tr>
                        <th className="text-left px-4 py-3">Priorità</th>
                        <th className="text-left px-4 py-3">Stato</th>
                        <th className="text-left px-4 py-3">Oggetto PEC</th>
                        <th className="text-left px-4 py-3">Mittente</th>
                        <th className="text-left px-4 py-3">Assegnato a</th>
                        <th className="text-left px-4 py-3">Op. risk</th>
                        <th className="text-left px-4 py-3">Aperto il</th>
                        <th className="text-left px-4 py-3">Azioni</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-[#DCE3EE]">
                    {items.map((r) => (
                        <tr key={r.id} data-testid={`review-row-${r.id}`} className="hover:bg-[#F5F7FB]">
                            <td className="px-4 py-3">{priorityBadge(r.priority)}</td>
                            <td className="px-4 py-3">{statusBadge(r.status)}</td>
                            <td className="px-4 py-3 max-w-xs truncate">{r.pec_subject || "—"}</td>
                            <td className="px-4 py-3 text-xs text-[#64748B]">{r.pec_from_address || "—"}</td>
                            <td className="px-4 py-3 text-xs text-[#172033]" data-testid={`review-assignee-${r.id}`}>{r.assigned_to_name || "—"}</td>
                            <td className="px-4 py-3 text-xs">{r.operational_risk ?? "—"}</td>
                            <td className="px-4 py-3 text-xs text-[#64748B]">{(r.opened_at || "").slice(0, 10)}</td>
                            <td className="px-4 py-3">
                                <button
                                    data-testid={`review-open-${r.id}`}
                                    onClick={() => onOpen(r.id)}
                                    className="text-xs text-[#2563EB] hover:underline">
                                    Apri
                                </button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function KanbanBoard({ columns, statuses, onChange, onOpen }) {
    const [dragging, setDragging] = useState(null);
    return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="reviews-kanban">
            {statuses.map((s) => (
                <div
                    key={s}
                    data-testid={`kanban-col-${s.replace(/\s+/g, "-").toLowerCase()}`}
                    className="bg-[#F5F7FB] rounded-lg p-2 min-h-[300px]"
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => { if (dragging) onChange(dragging, s); setDragging(null); }}
                >
                    <div className="px-2 py-1.5 flex items-center justify-between">
                        <div className="text-xs font-semibold text-[#172033]">{s}</div>
                        <div className="text-[11px] text-[#64748B]">{(columns[s] || []).length}</div>
                    </div>
                    <div className="space-y-2">
                        {(columns[s] || []).map((r) => (
                            <div
                                key={r.id}
                                data-testid={`kanban-card-${r.id}`}
                                draggable
                                onDragStart={() => setDragging(r.id)}
                                onClick={() => onOpen(r.id)}
                                className="bg-white border border-[#DCE3EE] rounded-lg p-3 cursor-grab hover:shadow-sm"
                            >
                                <div className="flex items-center justify-between mb-1">
                                    {priorityBadge(r.priority)}
                                </div>
                                <div className="text-xs font-medium text-[#172033] line-clamp-2">{r.pec_subject || "—"}</div>
                                <div className="text-[11px] text-[#64748B] mt-1 truncate">{r.pec_from_address}</div>
                            </div>
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );
}

export default function ReviewsPage() {
    const [view, setView] = useState("table");
    const [items, setItems] = useState([]);
    const [columns, setColumns] = useState({});
    const [statuses, setStatuses] = useState(STATUSES);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({ status: "", priority: "", assigned_to: "", search: "" });
    const navigate = useNavigate();

    const load = async () => {
        setLoading(true);
        try {
            if (view === "table") {
                const params = { limit: 500 };
                if (filters.status) params.status = filters.status;
                if (filters.priority) params.priority = filters.priority;
                if (filters.assigned_to) params.assigned_to = filters.assigned_to;
                if (filters.search) params.search = filters.search;
                const { data } = await api.get("/reviews", { params });
                setItems(data.items || []);
            } else {
                const { data } = await api.get("/reviews/kanban");
                setColumns(data.columns || {});
                setStatuses(data.statuses || STATUSES);
            }
        } catch (e) {
            toast.error("Errore caricamento revisioni");
        } finally { setLoading(false); }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [view]);

    const onKanbanChange = async (reviewId, newStatus) => {
        if (["Falso positivo", "Escalata"].includes(newStatus)) {
            const reason = window.prompt(`Motivo obbligatorio per stato "${newStatus}":`);
            if (!reason) return;
            try {
                await api.patch(`/reviews/${reviewId}/status`, { status: newStatus, reason });
                toast.success("Stato aggiornato");
                load();
            } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
            return;
        }
        try {
            await api.patch(`/reviews/${reviewId}/status`, { status: newStatus });
            toast.success("Stato aggiornato");
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    };

    const exportCsv = async () => {
        const params = new URLSearchParams();
        if (filters.status) params.set("status", filters.status);
        const suffix = params.toString();
        await downloadAuthenticated(`/reviews/export/csv${suffix ? "?" + suffix : ""}`, "revisioni.csv");
    };

    return (
        <div className="space-y-6">
            <PageHeader
                title="Coda revisioni"
                subtitle="Casi che richiedono verifica umana"
                breadcrumbs={[{ label: "Coda revisioni" }]}
                actions={(
                    <div className="flex items-center gap-2">
                        <button data-testid="reviews-export-csv" onClick={exportCsv}
                            className="inline-flex items-center gap-2 text-xs px-3 py-2 rounded-lg border border-[#DCE3EE] hover:bg-white">
                            <Download className="w-4 h-4" /> Esporta CSV
                        </button>
                        <div className="inline-flex items-center border border-[#DCE3EE] rounded-lg bg-white overflow-hidden">
                            <button data-testid="reviews-view-table" onClick={() => setView("table")}
                                className={`inline-flex items-center gap-1 px-3 py-2 text-xs ${view === "table" ? "bg-[#0B1633] text-white" : "text-[#172033]"}`}>
                                <List className="w-4 h-4" /> Tabella
                            </button>
                            <button data-testid="reviews-view-kanban" onClick={() => setView("kanban")}
                                className={`inline-flex items-center gap-1 px-3 py-2 text-xs ${view === "kanban" ? "bg-[#0B1633] text-white" : "text-[#172033]"}`}>
                                <LayoutGrid className="w-4 h-4" /> Kanban
                            </button>
                        </div>
                    </div>
                )}
            />
            <CompanyPecBanner />

            {view === "table" && (
                <div className="flex flex-wrap items-center gap-2">
                    <select data-testid="filter-status" value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                        className="text-xs border border-[#DCE3EE] rounded-md px-2 py-1.5 bg-white">
                        <option value="">Tutti gli stati</option>
                        {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <select data-testid="filter-priority" value={filters.priority} onChange={(e) => setFilters({ ...filters, priority: e.target.value })}
                        className="text-xs border border-[#DCE3EE] rounded-md px-2 py-1.5 bg-white">
                        <option value="">Tutte le priorità</option>
                        {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
                    </select>
                    <select data-testid="filter-assigned" value={filters.assigned_to} onChange={(e) => setFilters({ ...filters, assigned_to: e.target.value })}
                        className="text-xs border border-[#DCE3EE] rounded-md px-2 py-1.5 bg-white">
                        <option value="">Assegnazione</option>
                        <option value="me">Solo a me</option>
                        <option value="none">Non assegnati</option>
                    </select>
                    <input data-testid="filter-search" placeholder="Cerca oggetto o mittente…" value={filters.search}
                        onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                        className="text-xs border border-[#DCE3EE] rounded-md px-2 py-1.5 bg-white flex-1 max-w-xs" />
                    <button data-testid="filter-apply" onClick={load}
                        className="text-xs px-3 py-1.5 rounded-md bg-[#2563EB] text-white">Applica</button>
                </div>
            )}

            {view === "table"
                ? <ReviewsTable items={items} loading={loading} onOpen={(id) => navigate(`/reviews/${id}`)} />
                : <KanbanBoard columns={columns} statuses={statuses} onChange={onKanbanChange} onOpen={(id) => navigate(`/reviews/${id}`)} />
            }
        </div>
    );
}
